import React, { useState } from "react";
import { useLang } from "../../../i18n/LanguageContext";
import { useAuth } from "../../../auth/AuthContext";

const SESSION_EXPIRED_NOTICE_KEY = "morfeusz:auth:sessionExpiredNotice";

const getRedirectFromHash = () => {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) return null;
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  const target = params.get("redirect_to");
  if (!target) return null;
  if (target.startsWith("/")) return `#${target}`;
  if (target.startsWith("#")) return target;
  return null;
};

const consumeExpiredSessionNotice = () => {
  if (typeof window === "undefined") return false;
  let found = false;
  try {
    found = window.sessionStorage.getItem(SESSION_EXPIRED_NOTICE_KEY) === "1";
    window.sessionStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY);
  } catch (_) {
    /* ignore */
  }
  try {
    found = found || window.localStorage.getItem(SESSION_EXPIRED_NOTICE_KEY) === "1";
    window.localStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY);
  } catch (_) {
    /* ignore */
  }
  return found;
};

const copyForLang = (lang) => ({
  sessionExpired: lang === "en" ? "Session expired. Sign in again." : "Sesja wygasła. Zaloguj się ponownie.",
  activationOk: lang === "en" ? "Your account has been activated. You can sign in." : "Konto zostało aktywowane. Możesz się zalogować.",
  activationBad: lang === "en" ? "The activation link is invalid or has expired." : "Link aktywacyjny jest nieprawidłowy albo wygasł.",
  deletionPending: lang === "en"
    ? "The account has been marked for deletion. Sign-in is blocked. We sent a separate email with a link to cancel deletion — after cancelling, you can sign in again."
    : "Konto zostało oznaczone do usunięcia. Logowanie jest zablokowane. Wysłaliśmy osobny e-mail z linkiem cofnięcia decyzji — po cofnięciu możesz zalogować się ponownie.",
  deletionCancelled: lang === "en" ? "Account deletion has been cancelled. You can sign in now." : "Usunięcie konta zostało cofnięte. Możesz się teraz zalogować.",
  deletionLinkBad: lang === "en" ? "The deletion confirmation link is invalid or has expired." : "Link potwierdzenia usunięcia jest nieprawidłowy albo wygasł.",
  deletionCancelBad: lang === "en" ? "The cancellation link is invalid or has expired." : "Link cofnięcia usunięcia jest nieprawidłowy albo wygasł.",
  deletionError: lang === "en" ? "The account deletion link could not be handled." : "Nie udało się obsłużyć linku usunięcia konta.",
  deletePendingError: lang === "en"
    ? "This account is pending deletion. Sign-in is blocked. Check your email — we sent a cancellation link valid for 12 hours. After cancelling, you can sign in again."
    : "To konto oczekuje na usunięcie. Logowanie jest zablokowane. Sprawdź e-mail — wysłaliśmy link cofnięcia usunięcia ważny przez 12 godzin. Po cofnięciu możesz zalogować się ponownie.",
  emailNotConfirmed: lang === "en" ? "To sign in, confirm your email address first." : "Aby się zalogować, potwierdź najpierw swój adres e-mail.",
  vaultNotice: lang === "en"
    ? "This is your Morfeusz password. It opens your account and private dreams. Morfeusz does not know it — if you lose it, encrypted dreams cannot be recovered."
    : "To jest hasło Morfeusza. Otwiera konto i prywatne sny. Morfeusz go nie zna — jeśli je zgubisz, nie odzyskamy zaszyfrowanych snów.",
  identifierLabel: lang === "en" ? "Email or nickname" : "Email lub nick",
  identifierPlaceholder: lang === "en" ? "email@domain.com or nickname" : "email@domena.pl albo nick",
  passwordHelp: lang === "en"
    ? "Know your current password? After signing in, you can change it in Profile. Forgot it? The Morfeusz password cannot be recovered; email can only protect the account from unauthorized access."
    : "Znasz obecne hasło? Po zalogowaniu możesz je zmienić w Profilu. Nie pamiętasz hasła? Hasła Morfeusza nie da się odzyskać; e-mail może tylko zabezpieczyć konto przed cudzym dostępem.",
  eyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  loginTitlePre: lang === "en" ? "Return to your" : "Wróć do swojej",
  loginTitleAccent: lang === "en" ? "Dream Codex" : "Księgi Snów",
  password: lang === "en" ? "Password" : "Hasło",
  submitLogin: lang === "en" ? "Sign in" : "Zaloguj się",
  noAccount: lang === "en" ? "No account yet?" : "Nie masz konta?",
  signUpLink: lang === "en" ? "Create one" : "Załóż konto",
});

const getLoginNotice = (lang) => {
  if (typeof window === "undefined") return "";
  const copy = copyForLang(lang);
  const hash = window.location.hash || "";
  const queryStart = hash.indexOf("?");
  const params = queryStart >= 0 ? new URLSearchParams(hash.slice(queryStart + 1)) : new URLSearchParams();
  const activation = params.get("aktywacja");
  const deletion = params.get("usuniecie");
  const forceLogout = params.get("force_logout");
  const session = params.get("sesja");
  const expiredByMarker = consumeExpiredSessionNotice();

  if (forceLogout === "1") {
    try {
      window.localStorage.removeItem("morfeusz:auth:token");
      window.sessionStorage.removeItem("md_vault_session_blob_v1");
    } catch (_) {}
  }

  if (session === "wygasla" || expiredByMarker) return copy.sessionExpired;
  if (activation === "ok") return copy.activationOk;
  if (activation === "blad") return copy.activationBad;
  if (deletion === "zaplanowane") return copy.deletionPending;
  if (deletion === "cofniete") return copy.deletionCancelled;
  if (deletion === "link_nieprawidlowy") return copy.deletionLinkBad;
  if (deletion === "cofniecie_nieprawidlowe") return copy.deletionCancelBad;
  if (deletion === "blad") return copy.deletionError;
  return "";
};

export const LoginPage = () => {
  const { lang, t } = useLang();
  const { login, authMode } = useAuth();
  const copy = t.auth || {};
  const errs = (copy && copy.errors) || {};
  const localCopy = copyForLang(lang);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice] = useState(() => getLoginNotice(lang));
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!identifier.trim()) return setError(errs.required || "Required");
    if (!password) return setError(errs.required || "Required");
    setSubmitting(true);
    try {
      await login(identifier, password);
      const redirect = getRedirectFromHash();
      window.location.hash = redirect || "#/pokoik";
    } catch (err) {
      if (err.code === "account_delete_pending" || err.status === 423) setError(err.message || localCopy.deletePendingError);
      else if (err.code === "user_not_found" || err.code === "user-not-found") setError(errs.userNotFound || "User not found");
      else if (err.code === "wrong_password" || err.code === "wrong-password") setError(errs.wrongPassword || "Wrong password");
      else if (err.code === "email_not_confirmed") setError(localCopy.emailNotConfirmed);
      else setError(err.message || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mdPage" data-testid="login-page">
      <div className="mdPage__inner">
        <div>
          <p className="mdPage__eyebrow">{copy.eyebrow || localCopy.eyebrow}</p>
          <h1 className="mdPage__title">
            {copy.loginTitlePre || localCopy.loginTitlePre} <em>{copy.loginTitleAccent || localCopy.loginTitleAccent}</em>
          </h1>
          <p className="mdPage__subtitle">{copy.loginSubtitle}</p>
        </div>

        <form className="mdCard" onSubmit={handleSubmit} noValidate>
          {notice && (
            <div className="mdAlert mdAlert--info" data-testid="login-notice">
              {notice}
            </div>
          )}

          {error && (
            <div className="mdAlert" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="mdAlert mdAlert--info" data-ui="login-vault-password-notice">
            {localCopy.vaultNotice}
          </div>

          <div className="mdField">
            <label className="mdField__label" htmlFor="login-identifier">{localCopy.identifierLabel}</label>
            <input
              id="login-identifier"
              type="text"
              autoComplete="username"
              className="mdField__input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              data-testid="login-email"
              placeholder={localCopy.identifierPlaceholder}
              required
            />
          </div>

          <div className="mdField">
            <label className="mdField__label" htmlFor="login-password">{copy.password || localCopy.password}</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              className="mdField__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              required
            />
          </div>

          <button
            type="submit"
            className="mdBtn mdBtn--primary"
            disabled={submitting}
            data-testid="login-submit"
          >
            {submitting ? (copy.loading || "…") : (copy.submitLogin || localCopy.submitLogin)}
          </button>

          <p className="mdMuted" style={{ marginTop: 18 }}>
            {copy.noAccount || localCopy.noAccount}{" "}
            <a href="#/rejestracja" className="mdLink" data-testid="link-register">
              {copy.signUpLink || localCopy.signUpLink}
            </a>
          </p>

          <p className="mdMuted" data-ui="login-change-password-link">
            {localCopy.passwordHelp}
          </p>

          {authMode === "mock" && (
            <div className="mdAlert mdAlert--info" style={{ marginTop: 16 }}>
              {copy.mockNotice}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};
